import React from 'react';
import PropTypes from 'prop-types';
import { Dialog, DialogTitle, DialogContent, IconButton, Box, Typography } from '@mui/material';
import { withTranslation } from 'react-i18next';
import { Bar } from 'react-chartjs-2';
import { Chart, BarElement, CategoryScale, LinearScale, Tooltip, Legend } from 'chart.js';
import { BiX, BiArrowBack } from 'react-icons/bi';
import './DataSourceChart.scss';

Chart.register(BarElement, CategoryScale, LinearScale, Tooltip, Legend);

const MODAL_STYLE = {
  borderRadius: '24px',
  background: 'linear-gradient(135deg, rgba(255,255,255,0.55) 60%, rgba(59,130,246,0.10) 100%)',
  boxShadow: '0 8px 32px 0 rgba(31, 38, 135, 0.10)',
  backdropFilter: 'blur(18px) saturate(180%)',
  WebkitBackdropFilter: 'blur(18px) saturate(180%)',
  border: '1.5px solid rgba(255,255,255,0.25)',
  zIndex: 4000,
  minHeight: '60vh',
  maxHeight: '85vh',
  minWidth: '500px',
  width: '80vw',
  overflow: 'auto',
  scrollBehavior: 'smooth',
};

const DataSourceChart = ({ t, open, onClose, onBack, dataSourceName }) => {
  // Placeholder chart data
  const chartData = {
    labels: [
      t('Jan.label'), t('Feb.label'), t('Mar.label'), t('Apr.label'), t('May.label'), t('Jun.label'),
      t('Jul.label'), t('Aug.label'), t('Sep.label'), t('Oct.label'), t('Nov.label'), t('Dec.label')
    ],
    datasets: [
      {
        label: t('DataSourceChart.tCO2e.label'),
        data: [220, 310, 300, 320, 540, 330, 250, 340, 210, 260, 270, 275],
        backgroundColor: '#f59e42',
        borderRadius: 8,
        barPercentage: 0.7,
        categoryPercentage: 0.7,
      }
    ]
  };
  const chartOptions = {
    responsive: true,
    plugins: {
      legend: { display: false },
      tooltip: { enabled: true },
    },
    scales: {
      x: {
        grid: { display: false },
        ticks: { color: '#64748b', font: { size: 14 } }
      },
      y: {
        grid: { color: 'rgba(100,116,139,0.08)' },
        ticks: { color: '#64748b', font: { size: 14 } }
      }
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      className="data-source-chart-modal"
      PaperProps={{ style: MODAL_STYLE }}
      sx={{
        zIndex: 4000,
        '& .MuiBackdrop-root': { zIndex: 3999 }
      }}
    >
      <DialogTitle className="data-source-chart-modal-title" style={{ padding: 0 }}>
        <Box display="flex" alignItems="center" justifyContent="space-between" style={{ padding: 20 }}>
          <Box display="flex" alignItems="center">
            <IconButton
              onClick={onBack}
              className="back-button"
              size="small"
              title={t('DataSourceChart.back.label')}
            >
              <BiArrowBack />
            </IconButton>
            <Typography variant="h6" className="modal-title-text" style={{ marginLeft: 12, fontWeight: 600, color: '#334155' }}>
              {t('DataSourceChart.title.label')}
            </Typography>
          </Box>
          <IconButton onClick={onClose} className="close-btn" size="large" title={t('DataSourceChart.close.label')}>
            <BiX style={{ fontSize: 28 }} />
          </IconButton>
        </Box>
      </DialogTitle>
      <DialogContent className="data-source-chart-modal-content" style={{ display: 'flex', flexDirection: 'row', padding: 0, minHeight: '40vh' }}>
        {/* Filter Panel */}
        <div className="data-source-chart-filters">
          <Typography variant="h6" className="filters-title">{t('DataSourceChart.filters.label')}</Typography>
                      <div className="filter-group">
              <label>{t('DataSourceChart.period.label')}</label>
              <div className="filter-placeholder" />
              <div className="filter-placeholder" style={{ marginTop: 8 }} />
            </div>
            <div className="filter-group">
              <label>{t('DataSourceChart.parentEntity.label')}</label>
              <div className="filter-placeholder" />
            </div>
            <div className="filter-group">
              <label>{t('DataSourceChart.childEntity.label')}</label>
              <div className="filter-placeholder" />
            </div>
            <div className="filter-group">
              <label>{t('DataSourceChart.region.label')}</label>
              <div className="filter-placeholder" />
            </div>
            <div className="filter-group">
              <label>{t('DataSourceChart.compare.label')}</label>
              <div className="filter-placeholder" />
              <div className="filter-placeholder" style={{ marginTop: 8 }} />
            </div>
            <div className="filter-group">
              <label>{t('DataSourceChart.dataSources.label')}</label>
              <div className="filter-placeholder" />
            </div>
            <button className="reset-btn" type="button">{t('DataSourceChart.reset.label')}</button>
        </div>
        {/* Chart Area */}
        <div className="data-source-chart-area">
          <Typography variant="h5" className="chart-title" style={{ marginBottom: 6, fontWeight: 600, color: '#334155' }}>
            {dataSourceName || t('DataSourceChart.stationaryCombustion.label')}
          </Typography>
          <div className="chart-value-row" style={{ display: 'flex', alignItems: 'center', gap: 12, marginBottom: 6 }}>
            <span className="chart-value" style={{ fontSize: 28, fontWeight: 700, color: '#f59e42' }}>140</span>
            <span style={{ color: '#64748b', fontWeight: 500 }}>{t('DataSourceChart.tCO2e.label')}</span>
            <span className="chart-badge" style={{ background: '#d1fae5', color: '#059669', borderRadius: 6, padding: '3px 10px', fontWeight: 600, fontSize: 14, marginLeft: 6 }}>
              +12.3% {t('DataSourceChart.thanLastYear.label')}
            </span>
          </div>
          <div className="data-source-bar-chart">
            <Bar data={chartData} options={chartOptions} height={200} />
          </div>
          <div className="chart-footnote" style={{ color: '#64748b', fontSize: 12, marginTop: 12 }}>
            *GRI 307.1.1, VSME 112.3.2<br />
            <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4 }}>
              <span style={{ fontSize: 16 }}>⦿</span> {t('DataSourceChart.maximumInMay.label')}
            </span>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};

DataSourceChart.propTypes = {
  t: PropTypes.func.isRequired,
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onBack: PropTypes.func.isRequired,
  dataSourceName: PropTypes.string,
};

export default withTranslation()(DataSourceChart); 